import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { AppConstants } from '../../app.constants';
import { RumService } from '@spotlight/shared/rum';

@Injectable()
export class ErrorHandlerService {
  constructor(private readonly rum: RumService) { }

  public handleError(httpError: HttpErrorResponse | any): Observable<any> {
    let errMsg: string;
    if (httpError instanceof HttpErrorResponse) {
      // RUM already records the failed request as a resource.
      errMsg = this.getHttpErrorMessage(httpError);
    } else if (this.isResponseBody(httpError)) {
      // Sources that pass on `error.error` (the HTTP response body); RUM
      // already records the failed request as a resource.
      errMsg = this.extractErrorMessage(httpError);
    } else {
      errMsg = this.extractErrorMessage(httpError);
      this.rum.addError(httpError instanceof Error ? httpError : new Error(errMsg));
    }
    // The message is rethrown below; mark it so an uncaught rethrow isn't reported again.
    this.rum.markReported(errMsg);
    console.error(errMsg);
    return throwError(() => errMsg);
  }

  /**
   * Extracts a user-friendly error message from an error object without wrapping in Observable.
   * Useful when you need to pass error messages directly (e.g., to Subject.error()).
   * Prevents "[object Object]" from appearing in the UI.
   */
  public extractErrorMessage(error: any): string {
    // If it's already a string, return it
    if (typeof error === 'string') {
      return error;
    }

    if (typeof ProgressEvent !== 'undefined' && error instanceof ProgressEvent) {
      return AppConstants.genericNetworkError;
    }

    // Check if it's an HttpErrorResponse
    if (error instanceof HttpErrorResponse) {
      return this.getHttpErrorMessage(error);
    }

    // Try extracting from error.error property (common pattern from HttpClient)
    if (error && 'error' in error) {
      // If error.error is an empty string, return generic message immediately
      if (typeof error.error === 'string' && error.error.trim().length === 0) {
        return AppConstants.genericApiError || 'An unexpected error occurred. Please try again.';
      }

      const errorMessage = this.extractFromErrorProperty(error.error);
      if (errorMessage) {
        return errorMessage;
      }
    }

    // Try extracting from top-level properties
    const topLevelMessage = this.extractFromTopLevel(error);
    if (topLevelMessage) {
      return topLevelMessage;
    }

    // Generic error message if nothing else works
    return AppConstants.genericApiError || 'An unexpected error occurred. Please try again.';
  }

  private extractFromErrorProperty(errorObj: any): string | null {
    // If error.error is a non-empty string, use it
    if (typeof errorObj === 'string') {
      return errorObj.trim().length > 0 ? errorObj : null;
    }

    if (!errorObj || typeof errorObj !== 'object') {
      return null;
    }

    // Check for cfaError.userMessage (CFA API pattern)
    if (errorObj.cfaError?.userMessage) {
      return errorObj.cfaError.userMessage;
    }

    // Check for nested errors array
    if (errorObj.errors) {
      const messages = this.getNestedErrorsMessages(errorObj.errors);
      if (messages.length > 0) {
        return this.formatErrorsListMessage(messages);
      }
    }

    // Check for message property
    if (errorObj.message) {
      return errorObj.message;
    }

    // Try to stringify the object
    return this.tryStringifyError(errorObj);
  }

  private extractFromTopLevel(error: any): string | null {
    if (!error || typeof error !== 'object') {
      return null;
    }

    // Check for nested errors at top level
    if (error.errors) {
      const messages = this.getNestedErrorsMessages(error.errors);
      if (messages.length > 0) {
        return this.formatErrorsListMessage(messages);
      }
    }

    // Check for message at top level
    if (error.message) {
      return error.message;
    }

    // Try to stringify as last resort
    return this.tryStringifyError(error);
  }

  private tryStringifyError(error: any): string | null {
    if (error == null) {
      return null;
    }

    try {
      const stringified = JSON.stringify(error);
      if (stringified && stringified !== '{}' && stringified !== 'null') {
        return stringified;
      }
    } catch {
      // JSON.stringify failed
    }

    return null;
  }

  private getHttpErrorMessage(httpError: HttpErrorResponse): string {
    if (httpError?.error?.cfaError) {
      return httpError.error.cfaError.userMessage ? httpError.error.cfaError.userMessage : AppConstants.genericApiError;
    }
    const nestedErrorsMessages = this.getNestedErrorsMessages(httpError.error?.errors);
    if (nestedErrorsMessages.length > 0 && httpError?.url?.includes('/api/restaurant/campaigns')) {
      return this.formatErrorsListMessage(nestedErrorsMessages);
    }
    if (this.isAutomationAudienceTimeout(httpError)) {
      return AppConstants.genericTimeoutError;
    }
    if (httpError.error && (httpError.error.message || httpError.error.error)) {
      const err = httpError.error;
      const message = this.firstErrorLine(err.message);
      const detail = this.firstErrorLine(err.error);
      const parts = [message, detail].filter((part, index, all) =>
        part.length > 0 && all.indexOf(part) === index
      );
      return `${httpError.status} - ${parts.join(' ')}`.trim();
    }
    if (httpError.status !== 0) {
      return AppConstants.genericApiError;
    }
    if (httpError?.url?.includes('sendOffer')) {
      return AppConstants.rewardTimeoutError;
    } else if (httpError?.url?.includes('calendarEvent')) {
      return AppConstants.eventTimeoutError;
    } else if (httpError?.url?.includes('email')) {
      return AppConstants.emailTimeoutError;
    }
    return AppConstants.genericNetworkError;
  }

  private getNestedErrorsMessages(errors: unknown): string[] {
    if (!errors) {
      return [];
    }
    if (typeof errors === 'string') {
      return [errors];
    }
    if (Array.isArray(errors)) {
      const messages: string[] = [];
      for (const errorEntry of errors) {
        messages.push(...this.getNestedErrorsMessages(errorEntry));
      }
      return messages;
    }
    if (typeof errors === 'object') {
      const messages: string[] = [];
      for (const value of Object.values(errors as Record<string, unknown>)) {
        messages.push(...this.getNestedErrorsMessages(value));
      }
      return messages;
    }
    return [];
  }

  private formatErrorsListMessage(messages: string[]): string {
    const uniqueMessages = Array.from(new Set(messages));
    if (uniqueMessages.length === 1) {
      return uniqueMessages[0];
    }
    return uniqueMessages.map(message => `- ${message}`).join('\n');
  }

  private firstErrorLine(value: unknown): string {
    if (typeof value !== 'string') {
      return '';
    }
    return value.split(/\r?\n/).map(part => part.trim()).find(part => part.length > 0) ?? '';
  }

  /** A plain object (not an `Error` or `ProgressEvent`), which here is a failed request's response body. */
  private isResponseBody(error: unknown): boolean {
    return !!error && typeof error === 'object' && !(error instanceof Error)
      && !(typeof ProgressEvent !== 'undefined' && error instanceof ProgressEvent);
  }

  private isAutomationAudienceTimeout(httpError: HttpErrorResponse): boolean {
    return this.isAutomationAudienceRequest(httpError.url)
      && (httpError.status === 0 || httpError.status === 408 || httpError.status === 504);
  }

  private isAutomationAudienceRequest(url: string | null | undefined): boolean {
    return !!url && /\/automations\/campaigns\/[^/]+\/customers/.test(url);
  }
}
